import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getEditorAsync } from './registry'
import { ConfigJsonToggle } from './ConfigJsonToggle'

/**
 * Main orchestration component for the simulation visual editor.
 *
 * Reads initial values from formlib DOM elements, renders the appropriate
 * plugin editor, and syncs changes back to hidden textareas on every edit.
 */
export const SimulationEditor = ({ pluginSelectId, configTextareaId, tasksTextareaId }) => {
  const [plugin, setPlugin] = useState('')
  const [config, setConfig] = useState({})
  const [tasks, setTasks] = useState([])
  const [jsonMode, setJsonMode] = useState(false)
  const [parseError, setParseError] = useState(null)
  const configRowRef = useRef(null)
  const tasksRowRef = useRef(null)

  // Read initial state from DOM on mount
  useEffect(() => {
    const pluginSelect = document.getElementById(pluginSelectId)
    const configTextarea = document.getElementById(configTextareaId)
    const tasksTextarea = document.getElementById(tasksTextareaId)

    if (pluginSelect) {
      setPlugin(pluginSelect.value)
    }

    if (configTextarea) {
      try {
        const raw = configTextarea.value.trim()
        setConfig(raw ? JSON.parse(raw) : {})
      } catch (e) {
        setConfig({})
      }
      const row = configTextarea.closest('[data-name="plugin_config"]')
      if (row) {
        configRowRef.current = row
        row.style.display = 'none'
      }
    }

    if (tasksTextarea) {
      try {
        const raw = tasksTextarea.value.trim()
        setTasks(raw ? JSON.parse(raw) : [])
      } catch (e) {
        setTasks([])
      }
      const row = tasksTextarea.closest('[data-name="tasks"]')
      if (row) {
        tasksRowRef.current = row
        row.style.display = 'none'
      }
    }

    if (pluginSelect) {
      const handler = () => setPlugin(pluginSelect.value)
      pluginSelect.addEventListener('change', handler)
      return () => pluginSelect.removeEventListener('change', handler)
    }
  }, [pluginSelectId, configTextareaId, tasksTextareaId])

  // Sync config/tasks back to textareas on every change
  useEffect(() => {
    const configTextarea = document.getElementById(configTextareaId)
    if (configTextarea) {
      configTextarea.value = JSON.stringify(config, null, 2)
    }
  }, [config, configTextareaId])

  useEffect(() => {
    const tasksTextarea = document.getElementById(tasksTextareaId)
    if (tasksTextarea) {
      tasksTextarea.value = JSON.stringify(tasks, null, 2)
    }
  }, [tasks, tasksTextareaId])

  // Show/hide formlib rows based on JSON mode
  useEffect(() => {
    if (configRowRef.current) {
      configRowRef.current.style.display = jsonMode ? '' : 'none'
    }
    if (tasksRowRef.current) {
      tasksRowRef.current.style.display = jsonMode ? '' : 'none'
    }
  }, [jsonMode])

  const handleChange = useCallback((newConfig, newTasks) => {
    setConfig(newConfig)
    setTasks(newTasks)
  }, [])

  const [EditorComponent, setEditorComponent] = useState(null)
  const [editorLoading, setEditorLoading] = useState(false)

  // Load editor asynchronously when plugin changes
  useEffect(() => {
    if (!plugin) {
      setEditorComponent(null)
      return
    }

    let mounted = true
    setEditorLoading(true)
    setEditorComponent(null)

    getEditorAsync(plugin)
      .then(component => {
        if (!mounted) return
        setEditorComponent(() => component)
        setEditorLoading(false)
      })
      .catch(() => {
        if (!mounted) return
        setEditorComponent(null)
        setEditorLoading(false)
      })

    return () => { mounted = false }
  }, [plugin])

  const handleJsonToggle = useCallback((wantJson) => {
    if (wantJson) {
      setJsonMode(true)
      setParseError(null)
    } else {
      const configTextarea = document.getElementById(configTextareaId)
      const tasksTextarea = document.getElementById(tasksTextareaId)
      try {
        const rawConfig = configTextarea ? configTextarea.value.trim() : ''
        const rawTasks = tasksTextarea ? tasksTextarea.value.trim() : ''
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : {}
        const parsedTasks = rawTasks ? JSON.parse(rawTasks) : []
        if (rawTasks && !Array.isArray(parsedTasks)) {
          throw new Error('Tasks must be a JSON array')
        }
        setConfig(parsedConfig)
        setTasks(parsedTasks)
        setJsonMode(false)
        setParseError(null)
      } catch (e) {
        setParseError(`Cannot switch to visual mode: ${e.message}. Fix the JSON and try again.`)
      }
    }
  }, [configTextareaId, tasksTextareaId])

  const hasVisualEditor = !!EditorComponent

  return (
    <div style={{ marginTop: '12px' }}>
      <ConfigJsonToggle
        jsonMode={jsonMode}
        onToggle={handleJsonToggle}
        hasVisualEditor={hasVisualEditor}
        parseError={parseError}
      />
      {!jsonMode && editorLoading && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
          Loading editor...
        </div>
      )}
      {!jsonMode && !editorLoading && hasVisualEditor && (
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '16px',
          background: '#fff',
        }}>
          <EditorComponent
            config={config}
            tasks={tasks}
            onChange={handleChange}
          />
        </div>
      )}
      {!jsonMode && !editorLoading && !hasVisualEditor && (
        <div style={{
          padding: '16px',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          color: '#856404',
          fontSize: '14px',
        }}>
          No visual editor is available for the <strong>{plugin}</strong> plugin.
          Enable JSON Mode to edit the configuration and tasks directly.
        </div>
      )}
    </div>
  )
}
